/*
 * Copyright (c) 2024-2026 Nodals AI Ltd
 * All rights reserved.
 */

import { Logger } from '../logger';
import { Engine } from '../rules/engine';
import { GlobalFactStore } from '../rules/facts/store';
import { clone } from '../util';
import { ActivityManager } from '../activity/manager';

import type { Facts } from '../rules/index';
import type { AdData, KeyValue } from '../types';
import type { HierarchyNode } from '../activity/manager';

const logger = Logger.getInstance();

class Ad {
  private static readonly CACHE_TTL_MS = 3000;
  private data: AdData;
  private facts: Facts;
  private _keyValues: KeyValue[] = [];
  private _selectionLabel: string | undefined = undefined;
  private eligibilityCache: { result: boolean; timestamp: number } | null = null;

  constructor(adData: AdData, facts: Facts) {
    this.data = clone(adData);
    this.facts = clone(facts);
    this.setActivityFacts();
  }

  public get deliveryId(): string {
    return this.data.id;
  }

  public set keyValues(keyValues: KeyValue[]) {
    this._keyValues = clone(keyValues);
  }

  /**
   * Returns the linked line ID with backwards compatibility.
   * Priority: elid > geid > null
   */
  public get linkedLineId(): string | null {
    return this.data.elid ?? this.data.geid ?? null;
  }

  /**
   * Returns the linked creative ID.
   * Returns eid if available, otherwise falls back to geid if elid is not set.
   */
  public get linkedCreativeId(): string | null {
    // If we have eid, use it
    if (this.data.eid) {
      return this.data.eid;
    }
    // If we have geid but no elid, then geid might be the creative ID
    if (this.data.geid && !this.data.elid) {
      return this.data.geid;
    }
    return null;
  }

  public hasKeyValues(): boolean {
    return this._keyValues.length > 0;
  }

  public get keyValues(): KeyValue[] {
    return clone(this._keyValues);
  }

  public get selectionLabel(): string | undefined {
    return this._selectionLabel;
  }

  public set selectionLabel(label: string | undefined) {
    this._selectionLabel = label;
  }

  public isEligible(): boolean {
    if (!this.data?.rules?.conditions) {
      logger.warn(`Ad #${this.deliveryId} is missing conditions.`);
      return false;
    }

    const now = Date.now();

    if (this.eligibilityCache && now - this.eligibilityCache.timestamp <= Ad.CACHE_TTL_MS) {
      return this.eligibilityCache.result;
    }

    this.setActivityFacts();

    const allFacts = {
      ...GlobalFactStore.getInstance().getFacts(),
      ...this.facts,
    };

    const engine = new Engine(this.data?.rules?.conditions ?? {});
    const conditionsSatisfied = engine.run(allFacts);

    this.eligibilityCache = {
      result: conditionsSatisfied,
      timestamp: now,
    };

    return conditionsSatisfied;
  }

  public addFacts(facts: Facts): void {
    this.facts = { ...this.facts, ...facts };
    this.setActivityFacts();
    this.invalidateEligibilityCache();
  }

  private setActivityFacts(): void {
    const hierarchy: HierarchyNode = {
      id: this.deliveryId,
      children: [],
    };

    const activityManager = ActivityManager.getInstance();

    // Historical facts (30-day)
    this.facts['ad.imps'] = activityManager.getImpsForEntity(this.deliveryId, hierarchy);
    this.facts['ad.views'] = activityManager.getViewsForEntity(this.deliveryId, hierarchy);
    this.facts['ad.ints'] = activityManager.getInteractionsForEntity(this.deliveryId, hierarchy);

    // Page view facts (current page only)
    this.facts['ad.page.imps'] = activityManager.getImpsForEntity(
      this.deliveryId,
      hierarchy,
      true,
    ).length;
    this.facts['ad.page.views'] = activityManager.getViewsForEntity(
      this.deliveryId,
      hierarchy,
      true,
    ).length;
    this.facts['ad.page.ints'] = activityManager.getInteractionsForEntity(
      this.deliveryId,
      hierarchy,
      true,
    ).length;
  }

  private invalidateEligibilityCache(): void {
    this.eligibilityCache = null;
  }
}

export { Ad };
